(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initHeader = (root = document) => {
    const header = root.querySelector('[data-11p-header]') || document.querySelector('[data-11p-header]');
    if (!header || header.dataset.initialized === 'true') return;
    header.dataset.initialized = 'true';

    const dropdowns = [...header.querySelectorAll('[data-11p-dropdown]')];
    const closeDropdown = (item, returnFocus = false) => {
      item.classList.remove('is-open');
      const trigger = item.querySelector('.elevenp-header__nav-trigger');
      trigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger?.focus();
    };
    const closeAll = (except = null) => dropdowns.forEach((item) => item !== except && closeDropdown(item));
    const openDropdown = (item) => {
      closeAll(item);
      item.classList.add('is-open');
      item.querySelector('.elevenp-header__nav-trigger')?.setAttribute('aria-expanded', 'true');
    };

    dropdowns.forEach((item) => {
      const trigger = item.querySelector('.elevenp-header__nav-trigger');
      let timer;
      item.addEventListener('mouseenter', () => { window.clearTimeout(timer); openDropdown(item); });
      item.addEventListener('mouseleave', () => { timer = window.setTimeout(() => closeDropdown(item), 120); });
      trigger?.addEventListener('click', () => item.classList.contains('is-open') ? closeDropdown(item) : openDropdown(item));
      trigger?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openDropdown(item);
        item.querySelector('.elevenp-header__dropdown a')?.focus();
      });
      item.addEventListener('keydown', (event) => event.key === 'Escape' && closeDropdown(item, true));
      item.addEventListener('focusout', () => window.setTimeout(() => !item.contains(document.activeElement) && closeDropdown(item), 0));
    });

    document.addEventListener('click', (event) => !event.target.closest('[data-11p-dropdown]') && closeAll());

    const toggle = header.querySelector('[data-11p-mobile-toggle]');
    const menu = header.querySelector('[data-11p-mobile-menu]');
    const backdrop = document.querySelector('[data-11p-mobile-backdrop]');
    const setMenu = (open) => {
      menu?.classList.toggle('is-open', open);
      backdrop?.classList.toggle('is-visible', open);
      document.body.classList.toggle('elevenp-menu-open', open);
      toggle?.setAttribute('aria-expanded', String(open));
      toggle?.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      menu?.setAttribute('aria-hidden', String(!open));
      toggle?.querySelector('[data-11p-menu-icon]')?.toggleAttribute('hidden', open);
      toggle?.querySelector('[data-11p-close-icon]')?.toggleAttribute('hidden', !open);
      if (!open) menu?.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
    };
    toggle?.addEventListener('click', () => setMenu(toggle.getAttribute('aria-expanded') !== 'true'));
    backdrop?.addEventListener('click', () => setMenu(false));
    menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && toggle?.getAttribute('aria-expanded') === 'true') {
        setMenu(false);
        toggle.focus();
      }
    });
    window.addEventListener('resize', () => window.innerWidth >= 1180 && setMenu(false));
    window.addEventListener('scroll', () => header.classList.toggle('is-scrolled', window.scrollY > 8), { passive: true });
  };

  const initHero = (root = document) => {
    const heroes = root.matches?.('[data-11p-hero]') ? [root] : [...root.querySelectorAll('[data-11p-hero]')];
    heroes.forEach((hero) => {
      if (hero.dataset.initialized === 'true') return;
      hero.dataset.initialized = 'true';
      const slides = [...hero.querySelectorAll('[data-11p-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-11p-hero-dot]')];
      if (slides.length < 2) return;
      const mobileSingleSlide = window.matchMedia('(max-width: 767px)');
      let active = 0;
      let swipeStart = null;
      const show = (requested) => {
        const target = mobileSingleSlide.matches ? 0 : requested;
        active = (target + slides.length) % slides.length;
        slides.forEach((slide, index) => {
          const isActive = index === active;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', String(!isActive));
        });
        dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === active)));
      };
      hero.querySelector('[data-11p-hero-previous]')?.addEventListener('click', () => show(active - 1));
      hero.querySelector('[data-11p-hero-next]')?.addEventListener('click', () => show(active + 1));
      dots.forEach((dot, index) => dot.addEventListener('click', () => show(index)));
      hero.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') show(active - 1);
        if (event.key === 'ArrowRight') show(active + 1);
      });
      hero.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse') swipeStart = { x: event.clientX, y: event.clientY };
      });
      hero.addEventListener('pointerup', (event) => {
        if (!swipeStart || event.pointerType === 'mouse') return;
        const dx = event.clientX - swipeStart.x;
        const dy = event.clientY - swipeStart.y;
        swipeStart = null;
        if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.2) show(active + (dx < 0 ? 1 : -1));
      });
      hero.addEventListener('pointercancel', () => { swipeStart = null; });
      const syncMobileMode = () => show(active);
      if (mobileSingleSlide.addEventListener) mobileSingleSlide.addEventListener('change', syncMobileMode);
      else mobileSingleSlide.addListener(syncMobileMode);
      show(0);
    });
  };

  const initSolutions = (root = document) => {
    const carousels = root.matches?.('[data-11p-solutions]') ? [root] : [...root.querySelectorAll('[data-11p-solutions]')];
    carousels.forEach((carousel) => {
      if (carousel.dataset.initialized === 'true') return;
      carousel.dataset.initialized = 'true';
      const track = carousel.querySelector('[data-11p-solutions-track]');
      const previous = carousel.querySelector('[data-11p-solutions-previous]');
      const next = carousel.querySelector('[data-11p-solutions-next]');
      const cards = [...track?.children || []];
      if (!track || cards.length < 2) return;
      const getStep = () => cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).gap) || 0);
      const getIndex = () => Math.max(0, Math.min(cards.length - 1, Math.round(track.scrollLeft / getStep())));
      const update = () => {
        const index = getIndex();
        if (previous) previous.disabled = index === 0;
        if (next) next.disabled = index >= cards.length - 1;
      };
      const move = (direction) => track.scrollTo({ left: (getIndex() + direction) * getStep(), behavior: reducedMotion ? 'auto' : 'smooth' });
      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  };

  const init = (root = document) => {
    initHeader(root);
    initHero(root);
    initSolutions(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
